/**
 * Google Analytics 4. Off unless the build sets REACT_APP_GA_MEASUREMENT_ID, and off for a browser
 * sending Do Not Track or Global Privacy Control — the privacy policy (portal `/privacy`) promises both.
 *
 * Google receives a page's path and nothing after it. Query strings here carry bearer material: a
 * voucher's QR code opens `/voucher?code=<code>`, and `/thank-you?session_id=` unlocks the donor's
 * receipt. So no page view is left to gtag's own location reading: `page_location` is set to
 * origin + path before every event, and the stream's enhanced-measurement "page changes based on
 * browser history events" must stay off, or gtag sends the full URL on each navigation.
 */
type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
  interface Navigator {
    readonly globalPrivacyControl?: boolean;
  }
}

const trackingDeclined = (): boolean =>
  navigator.doNotTrack === '1' || navigator.globalPrivacyControl === true;

const pageLocation = (pathname: string): string => `${window.location.origin}${pathname}`;

/** Load gtag.js once. Page views are sent by `trackPageView`, not by the config. */
export const initAnalytics = (measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID): boolean => {
  if (!measurementId || window.gtag || trackingDeclined()) return false;
  const dataLayer = (window.dataLayer ??= []);
  // gtag.js reads each queued command as an `arguments` object; a pushed array is ignored.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('set', { page_location: pageLocation(window.location.pathname) });
  window.gtag('config', measurementId, { send_page_view: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
  return true;
};

/**
 * Record a page view for a route, and re-point `page_location` for every later event (engagement,
 * scrolls, clicks, the thank-you page's donation events) to the same path-only URL.
 */
export const trackPageView = (pathname: string): void => {
  if (!window.gtag) return;
  const page_location = pageLocation(pathname);
  window.gtag('set', { page_location });
  window.gtag('event', 'page_view', { page_location, page_title: document.title });
};
